import { Col, Clickable, FollowRequiredModal, Icon, ModalV2, Reactions, ReactToPost, Row, Text } from 'components';
import { Api, normalizeDate, theme } from 'lib';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

const Wrapper = styled.div`
  height: 159px;
  width: 296px;
  background-color: ${theme.GREY_10};
  display: flex;
  flex-direction: column;
  overflow: hidden;

  ${(props) =>
    props.wide &&
    `
    height: fit-content;
    width: 100%
  `}
`;

const NormalText = styled.span`
  font-weight: 400;
`;

const ContentArea = styled.div`
  padding: 16px;
  height: 100%;
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: space-between;
`;

const DeleteBtn = styled.button`
  position: absolute;
  top: 6px;
  right: 6px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 2px;
  opacity: 0.5;
  line-height: 1;
  &:hover { opacity: 1; }
`;

const ConfirmActions = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;
`;

const ConfirmButton = styled.button`
  flex: 1;
  height: 44px;
  border-radius: 999px;
  border: 1px solid ${(props) => (props.variant === 'danger' ? '#E53935' : theme.GREY_30)};
  background: ${(props) => (props.variant === 'danger' ? '#E53935' : '#fff')};
  color: ${(props) => (props.variant === 'danger' ? '#fff' : theme.GREY_80)};
  font-weight: 700;
  cursor: pointer;
  opacity: ${(props) => (props.disabled ? 0.6 : 1)};
`;

const Shoutout = ({ item = {}, user, handleClick = () => null, wide, dontShowReact, onDelete }) => {
  const shoutoutFromStore = useSelector((state) => state.shoutouts.find((x) => x._id === item._id));
  item = item || shoutoutFromStore;

  const [reactionsVisible, setReactionsVisible] = useState(false),
    [reactionLoading, setReactionLoading] = useState(false),
    [followRequiredVisible, setFollowRequiredVisible] = useState(false),
    [deleting, setDeleting] = useState(false),
    [showDeleteModal, setShowDeleteModal] = useState(false),
    currentUser = useSelector((state) => state.user),
    { t } = useTranslation(),
    dispatch = useDispatch();

  if (!item) return null;

  const sender = typeof item?.from === 'object' ? item.from : user;
  const to = typeof item?.user === 'object' ? item.user : user;
  const isOwner = currentUser?._id &&
    (typeof item?.from === 'object' ? item.from?._id : item.from) === currentUser._id;
  const prevReaction = currentUser?.reactions?.find((x) => x.entityId === item._id)?.reaction;

  const teamId = typeof item?.user === 'object' ? item?.user?._id : item?.user;
  const isFollowing = currentUser?.following?.includes(teamId?.toString());

  async function react(reaction) {
    if (reactionLoading || !currentUser?._id) return;

    if (!isFollowing) {
      setFollowRequiredVisible(true);
      return;
    }
    setReactionLoading(true);
    setReactionsVisible(false);

    const data = {
      entityType: 'shoutout',
      reaction,
      prevReaction,
      entityId: item._id,
      userId: currentUser?._id
    };

    handleClick(data);

    dispatch({
      type: 'ADD_REACTION_TO_SHOUTOUT',
      payload: data
    });

    const res = await Api.post('/users/react', data);
    if (!res) return;

    setReactionLoading(false);
  }

  async function handleDelete() {
    setDeleting(true);
    const res = await Api.delete(`/shoutouts/delete/${item._id}`);
    setDeleting(false);
    if (res) {
      onDelete?.(item._id);
    }
    setShowDeleteModal(false);
  }

  if (!to || !sender) return null;

  return (
    <React.Fragment>
      <FollowRequiredModal
        visible={followRequiredVisible}
        onClose={() => setFollowRequiredVisible(false)}
        teamName={to?.name || to?.username}
        feature={t('common.react')}
      />
      <ModalV2
        visible={showDeleteModal}
        closeModal={() => !deleting && setShowDeleteModal(false)}
        width="420px"
      >
        <Col pad="8px" gap="16px">
          <Text h3 bold>
            Delete shoutout?
          </Text>
          <Text col={theme.GREY_60}>
            Are you sure you want to delete this shoutout? Any points earned will be deducted.
          </Text>
          <ConfirmActions>
            <ConfirmButton
              type="button"
              onClick={() => setShowDeleteModal(false)}
              disabled={deleting}
            >
              Cancel
            </ConfirmButton>
            <ConfirmButton
              type="button"
              variant="danger"
              onClick={handleDelete}
              disabled={deleting}
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </ConfirmButton>
          </ConfirmActions>
        </Col>
      </ModalV2>

      <Wrapper wide={wide} style={{ position: 'relative' }}>
        {isOwner && (
          <DeleteBtn onClick={() => setShowDeleteModal(true)} disabled={deleting} title="Delete shoutout">
            <Icon type="close" dimensions={10} col={theme.GREY_60} />
          </DeleteBtn>
        )}
        <ContentArea>
          <Row>
            <Col start>
              <Row>
                <Text b2 bold wid="264px">
                  {to?.username && (!item.media || !item.youtubeId) ? to.username + ' ' : ''}
                  <NormalText>from </NormalText>
                  {sender?.username}
                </Text>
              </Row>

              <Text
                marg="4px 0 0"
                wordBreak="break-word"
                whiteSpace="pre-wrap"
                lineClamp={wide ? undefined : 4}
                textOverflow="ellipsis"
              >
                {item.comment}
              </Text>
            </Col>
          </Row>

          <Col noFlex endAll wid="100%" center marg={wide ? '13px 0 0' : '0'}>
            <Row between noFlex wid="100%">
              <Row center ht="18px">
                {!dontShowReact && (
                  <Row noFlex marg="0 12px 0 0">
                    <ReactToPost
                      reaction={prevReaction}
                      setReactionsVisible={setReactionsVisible}
                      reactionsVisible={reactionsVisible}
                      handleSubmit={(e) => react(e)}
                      small
                    />
                  </Row>
                )}

                <Reactions small entity={item.reactionCounts} />
              </Row>

              <Row>
                <Text c1 med col={theme.GREY_60}>
                  {item.createdAt && normalizeDate(new Date(item.createdAt), 'Default', t)}
                </Text>
              </Row>
            </Row>
          </Col>
        </ContentArea>
      </Wrapper>
    </React.Fragment>
  );
};

export default Shoutout;
